import { ReactNode, useId, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { MOBILE_TAP_TARGET } from '../bookingMobile';

interface Props {
  /** El rótulo de la sección: en desktop se lee, en mobile además se toca. */
  title: string;
  /**
   * Arranca plegada y se despliega con un toque. Solo en mobile y solo para el
   * repaso ya leído (`collapsedBookingSectionsOnMobile`): nada que frene el CTA
   * llega acá en `true`.
   */
  collapsed: boolean;
  children: ReactNode;
}

/**
 * Una sección del flujo con su título.
 *
 * En desktop es exactamente lo que era: el título y el contenido debajo, sin
 * un control de más. En mobile —y solo para el repaso, con la reserva ya
 * creada— el título pasa a ser el botón que la despliega: la pantalla de 375
 * no alcanza para todo, así que lo que ya se leyó cede el lugar a lo que
 * todavía se decide.
 *
 * El título lo dibuja ESTE componente en los dos casos, no la sección: si cada
 * uno pusiera el suyo, el plegado tendría que esconder un título que en
 * desktop hay que mostrar, y terminarían siendo dos.
 */
export function BookingSectionDisclosure({ title, collapsed, children }: Props) {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  // El contenido va siempre en la misma pila: el espaciado entre sus bloques
  // no puede depender de si la sección se pliega o no.
  const content = (
    <Box display="flex" flexDirection="column" gap={2.5}>
      {children}
    </Box>
  );

  if (!collapsed) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        {content}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((previous) => !previous)}
        sx={{
          width: '100%',
          minHeight: MOBILE_TAP_TARGET,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 0,
          py: 0.5,
          border: 0,
          background: 'none',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <ExpandMore
          fontSize="small"
          sx={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </Box>
      <Collapse in={open} id={contentId}>
        <Box pt={1}>{content}</Box>
      </Collapse>
    </Box>
  );
}
